import keyBy from 'lodash/keyBy';
import type { MutationError } from '@atlassian/jira-business-error-handling';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import type { JiraBusinessFormWithFieldData } from '../get-form/types';
import type { CreateJiraBusinessIssueByForm } from './__generated_apollo__/CreateJiraBusinessIssueByForm';
import type {
	FormCreateIssue,
	JiraIssueFieldsInputWritable,
	JiraLabelsFieldInputWritable,
	FormInputData,
} from './types';
import writersMap from './writers-map';
import { toLabelsField } from './writers-map/utils';

const FORM_LABEL = 'business-form';

export const transformResult = (
	data: CreateJiraBusinessIssueByForm | null | undefined,
): FormCreateIssue | undefined | null => {
	if (!data) {
		return data;
	}

	const issueId = data.createJiraBusinessIssueByForm?.item?.issueId;

	const isIssueIdValid = typeof issueId === 'number';
	const isCreateJiraBusinessIssueSuccessValid =
		typeof data.createJiraBusinessIssueByForm?.success === 'boolean';

	if (!isIssueIdValid || !isCreateJiraBusinessIssueSuccessValid) {
		fireErrorAnalytics({
			meta: {
				id: 'transformFormSubmissionResult',
				packageName: 'jiraBusinessForm',
				teamName: 'jira-warepil',
			},
			attributes: {
				message: 'Some fields from form submission were not returned properly',
				isIssueIdValid,
				isCreateJiraBusinessIssueSuccessValid,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	if (typeof data.createJiraBusinessIssueByForm?.success !== 'boolean') {
		return undefined;
	}

	return {
		success: data.createJiraBusinessIssueByForm.success,
		issueId,
	};
};

/**
 * Transform the form submission data, (leveraging any other necessary configuration)
 * so it can be massaged into "fields" shape and sent to the endpoint.
 */
type TransformFormDataType = {
	formWithFieldData: JiraBusinessFormWithFieldData;
	formInputData: FormInputData;
};
export const transformFormData = ({ formInputData, formWithFieldData }: TransformFormDataType) => {
	const formFieldsById = keyBy(formWithFieldData.fields, (f) => f.fieldId);
	const fields: JiraIssueFieldsInputWritable = {
		users: [],
		labels: [],
		versions: [],
		stringFields: [],
		adfFields: [],
		multipleSelectFields: [],
		singleGroupPickerFields: [],
		multipleGroupPickerFields: [],
		multipleSelectUserPickerFields: [],
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Object.entries(formInputData).forEach(([key, value]: [any, any]) => {
		if (value === undefined || value === null) {
			return;
		}

		const fieldData = formFieldsById[key];
		if (!fieldData) {
			return;
		}

		const { type } = fieldData;

		if (type in writersMap === false) {
			fireErrorAnalytics({
				meta: {
					id: 'transformFormSubmissionResult',
					packageName: 'jiraBusinessForm',
					teamName: 'jira-warepil',
				},
				attributes: {
					message: `"${type}" was unexpectedly missing for field mapping in form submission`,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return;
		}
		const { writeTo } = writersMap[type];
		writeTo(key, value, fields);
	});

	// If a label (FORM_LABEL) doesn't exist, add it.
	const labelsField: JiraLabelsFieldInputWritable | undefined = fields.labels.find(
		(f) => f.fieldId === 'labels',
	);
	if (labelsField) {
		if (!labelsField.values.values.includes(FORM_LABEL)) {
			labelsField.values.values.push(FORM_LABEL);
		}
	} else {
		fields.labels.push(toLabelsField('labels', [FORM_LABEL]));
	}

	return fields;
};

export const transformErrorMessage = (
	err: MutationError,
	formWithFieldData: JiraBusinessFormWithFieldData | undefined,
) => {
	const fieldWithError = formWithFieldData?.fields?.find(
		(field) => field.fieldId === err?.extensions?.fieldId,
	);
	const errorMessage = fieldWithError?.alias
		? err?.message.replace(fieldWithError.name, fieldWithError.alias)
		: err?.message;
	return errorMessage;
};
